import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../root/root-types';
import {
  AddNotificationPayload,
  AppNotification,
  NotificationsSliceState,
} from './notifications-model';

export const NOTIFICATIONS_FEATURE_KEY = 'notifications';
const MAX_NOTIFICATIONS = 100;

export const initialNotificationsState: NotificationsSliceState = {
  items: [],
};

const getDefaultNotificationId = () =>
  `notif-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const notificationsSlice = createSlice({
  name: NOTIFICATIONS_FEATURE_KEY,
  initialState: initialNotificationsState,
  reducers: {
    addNotification(state, action: PayloadAction<AddNotificationPayload>) {
      const payload = action.payload;

      if (!payload.title?.trim() && !payload.body?.trim()) {
        return;
      }

      const id = payload.id || getDefaultNotificationId();

      if (state.items.some((item) => item.id === id)) {
        return;
      }

      const notification: AppNotification = {
        id,
        title: payload.title,
        body: payload.body,
        type: payload.type,
        navigationPath: payload.navigationPath,
        source: payload.source || 'foreground',
        read: false,
        createdAt: payload.createdAt || new Date().toISOString(),
        data: payload.data,
      };

      state.items.unshift(notification);
      if (state.items.length > MAX_NOTIFICATIONS) {
        state.items.length = MAX_NOTIFICATIONS;
      }
    },
    markNotificationRead(state, action: PayloadAction<string>) {
      const id = action.payload;
      const notification = state.items.find((item) => item.id === id);
      if (notification) {
        notification.read = true;
      }
    },
    markAllNotificationsRead(state) {
      state.items.forEach((item) => {
        item.read = true;
      });
    },
    removeNotification(state, action: PayloadAction<string>) {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    clearNotifications(state) {
      state.items = [];
    },
  },
});

export const {
  addNotification,
  markNotificationRead,
  markAllNotificationsRead,
  removeNotification,
  clearNotifications,
} = notificationsSlice.actions;

export const getNotificationsSliceState = (
  rootState: RootState
): NotificationsSliceState => rootState[NOTIFICATIONS_FEATURE_KEY];

export const getNotifications = createSelector(
  getNotificationsSliceState,
  (state) => state.items
);

export const getUnreadNotificationsCount = createSelector(
  getNotificationsSliceState,
  (state) => state.items.filter((item) => !item.read).length
);

export const getLatestNotification = createSelector(
  getNotificationsSliceState,
  (state) => state.items[0] || null
);
